/**
 * Feature: Roofing Project Management
 * /features/project-management
 */
import {
  FeatureFaq,
  FeatureStats,
  FeatureSignup,
  FeatureHeroBadge,
  FeatureChecklist,
} from '@/components/marketing/FeaturePageSections';

import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { Link } from 'react-router-dom';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import {
  LayoutGrid,
  Users,
  Camera,
  Package,
  MessageSquare,
  ClipboardList,
  ArrowRight,
} from 'lucide-react';
import { CapabilityLedger } from '@/components/marketing/CapabilityLedger';

const faqs = [
  {
    question: 'Can subcontractors use Rooflyzer?',
    answer:
      'Yes. Invite subs as limited users. They can access their assigned jobs, log photos, and update job status without seeing your full CRM.',
  },
  {
    question: 'How many active jobs can I track?',
    answer: 'Unlimited on Pro and Scale plans. Free plan supports up to 10 active jobs.',
  },
  {
    question: 'Does Rooflyzer integrate with material suppliers?',
    answer:
      'We support CSV material imports and are building direct integrations with major distributors. Subscribe to our changelog for updates.',
  },
  {
    question: 'Can I track permit status?',
    answer:
      'Yes. Log permit pull dates, inspection appointments, and pass/fail status for every job.',
  },
  {
    question: 'Does the mobile app work offline?',
    answer:
      'Yes. Take photos, update job stages, and log notes in areas without cell service. Data syncs automatically when you reconnect.',
  },
];

const projectManagementStats = [
  {
    value: '6 stages',
    label: 'Standard Job Pipeline',
    desc: 'From signed to complete',
  },
  {
    value: '3x faster',
    label: 'Customer Communication',
    desc: 'With automated updates',
  },
  {
    value: '0 disputes',
    label: 'Job Record Conflicts',
    desc: 'With timestamped photo docs',
  },
  { value: 'Real-time', label: 'Crew Scheduling', desc: 'Across all active jobs' },
];

export const ProjectManagementPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Roofing Project Management',
    description:
      'Manage every roofing job from signed contract to final payment. Track crews, materials, milestones, photos, and customer communication in one place.',
    featureList: [
      '6-stage job pipeline',
      'Crew assignment & scheduling',
      'Before, during & after photos',
      'Materials tracking',
      'Customer status updates',
      'Inspection & punch lists',
    ],
    url: 'https://rooflyzer.pro/features/project-management',
    offers: { price: '0', description: 'Free tier available — no credit card required' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing Project Management Software — Job to Completion"
        description="Manage every roofing job from signed contract to final payment. Track crews, materials, milestones, photos, and customer communication in one place."
        canonicalUrl="/features/project-management"
        keywords="roofing project management software, roofing job management, roofing job tracking, roofing contractor management"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Project Management', url: '/features/project-management' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={LayoutGrid}>Zero Jobs Fall Through</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Every Roofing Job —{' '}
              <span className="text-white underline decoration-slate-600 decoration-2 underline-offset-8">
                On Time, On Budget, On Record
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Track every job through a 6-stage pipeline. Assign crews, log materials, document with
              photos, and communicate with customers — all in one platform built for roofing.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-[#050507] hover:bg-white/90 font-semibold px-8"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Manage Jobs Free
                <ArrowRight className="ml-2 size-5" />
              </Button>
              <Link to="/features">
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/20 text-white hover:bg-white/10"
                >
                  See the Pipeline
                </Button>
              </Link>
            </div>
          </section>

          {/* Product Screenshot */}
          <section className="mb-24">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
              <img
                src="/product-frames/dashboard.webp"
                alt="Rooflyzer project management view"
                className="w-full h-auto"
                loading="lazy"
              />
              <div className="absolute bottom-6 left-6 z-20">
                <span className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-sm text-white/90 border border-white/20">
                  Live product screenshot
                </span>
              </div>
            </div>
          </section>

          {/* Capability Grid */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-12">
              Built for Every Stage of the Job
            </h2>
            <CapabilityLedger
              items={[
                {
                  icon: LayoutGrid,
                  title: '6-Stage Job Pipeline',
                  desc: 'Move jobs through: Signed → Materials Ordered → Permit Pulled → Scheduled → In Production → Complete. Visual Kanban board for your whole team.',
                },
                {
                  icon: Users,
                  title: 'Crew Assignment & Scheduling',
                  desc: 'Assign crews to jobs, view crew calendars, and avoid scheduling conflicts across multiple properties.',
                },
                {
                  icon: Camera,
                  title: 'Before, During & After Photos',
                  desc: 'Capture job site photos from mobile. Organized by job, date-stamped, and stored permanently for warranty and dispute protection.',
                },
                {
                  icon: Package,
                  title: 'Materials Tracking',
                  desc: "Log what's ordered, what's delivered, and what's on-site for every job. No more over-ordering or supply surprises.",
                },
                {
                  icon: MessageSquare,
                  title: 'Customer Status Updates',
                  desc: 'Send automated SMS or email updates when jobs hit milestones. Customers stay informed without you having to call.',
                },
                {
                  icon: ClipboardList,
                  title: 'Inspection & Punch Lists',
                  desc: 'Create custom inspection checklists and punch lists. Sign off on completion criteria before closing a job.',
                },
              ]}
            />
          </section>

          {/* Problem → Solution */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Old Way</h2>
                <ul className="space-y-4">
                  {[
                    'Track jobs in spreadsheets updated manually',
                    "Miss scheduling conflicts until it's too late",
                    'Lose job site photos across different phones',
                    'Forget to update customers, generate angry callbacks',
                    'Disputes about what was done and when — no record',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/80">
                      <span className="text-red-400 mt-0.5 text-lg">✕</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">The Rooflyzer Way</h2>
                <FeatureChecklist
                  items={[
                    'Visual Kanban pipeline shows every job stage instantly',
                    'Automated customer updates at every milestone',
                    'All photos organized by job, timestamped and searchable',
                    'Crew scheduling calendar with conflict detection',
                    'Dispute-proof job records with photo evidence',
                  ]}
                />
              </div>
            </div>
          </section>

          {/* Stats */}
          <FeatureStats title="Real Numbers" items={projectManagementStats} />

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* Bottom CTA */}
          <FeatureSignup
            title="Ready to Run Every Job Like a Pro?"
            buttonLabel="Manage Jobs Free — No Credit Card"
          >
            No more spreadsheets. No more missed updates. No more disputes. One platform, every job.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default ProjectManagementPage;
